import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { listNodes } from '@/api/nodes'
import { usePolling } from '@/hooks/usePolling'
import type { NodeSummary } from '@/types'
import NodeTable from '@/components/NodeTable'
import NodeFilterBar from '@/components/NodeFilterBar'
import Pagination from '@/components/Pagination'

const PAGE_SIZE = 10
const METADATA_LIMIT = 1000

interface Filters {
  status: string
  company: string
  site: string
}

export default function NodeListPage() {
  const [nodes, setNodes] = useState<NodeSummary[]>([])
  const [allNodes, setAllNodes] = useState<NodeSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<Filters>(() => ({
    status: searchParams.get('status') ?? '',
    company: searchParams.get('company') ?? '',
    site: searchParams.get('site') ?? '',
  }), [searchParams])

  const page = useMemo(() => {
    const raw = Number(searchParams.get('page') ?? '1')
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1
  }, [searchParams])

  const setFilters = useCallback((nextFilters: Filters) => {
    const nextParams = new URLSearchParams(searchParams)

    for (const key of ['status', 'company', 'site'] as const) {
      const value = nextFilters[key].trim()
      if (value) {
        nextParams.set(key, value)
      } else {
        nextParams.delete(key)
      }
    }
    nextParams.delete('page')

    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const setPage = useCallback((nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams)
    if (nextPage <= 1) {
      nextParams.delete('page')
    } else {
      nextParams.set('page', String(nextPage))
    }
    setSearchParams(nextParams)
  }, [searchParams, setSearchParams])

  const fetchNodes = useCallback(async () => {
    try {
      const [pageRes, metadataRes] = await Promise.all([
        listNodes({
          ...filters,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        }),
        listNodes({ limit: METADATA_LIMIT }),
      ])
      const companyExists = !filters.company || metadataRes.items.some((item) => item.company === filters.company)
      const siteExists = !filters.site || metadataRes.items.some((item) => (
        (!filters.company || item.company === filters.company) && item.site === filters.site
      ))
      if (!companyExists || !siteExists) {
        setFilters({ ...filters, company: companyExists ? filters.company : '', site: '' })
        return
      }
      setNodes(pageRes.items)
      setTotal(pageRes.total)
      setAllNodes(metadataRes.items)
      setError(null)
    } catch {
      setError('Failed to load nodes.')
    } finally {
      setLoading(false)
    }
  }, [filters, page, setFilters])

  useEffect(() => {
    setLoading(true)
    fetchNodes()
  }, [fetchNodes])

  usePolling(fetchNodes, 30000, !loading)

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    if (!loading && page > totalPages) {
      setPage(totalPages)
    }
  }, [loading, page, setPage, total])

  const sites = useMemo(
    () => Array.from(new Set(
      allNodes
        .filter((n) => !filters.company || n.company === filters.company)
        .map((n) => n.site)
        .filter(Boolean) as string[],
    )).sort(),
    [allNodes, filters.company],
  )

  const companies = useMemo(
    () => Array.from(new Set(allNodes.map((n) => n.company).filter(Boolean) as string[])).sort(),
    [allNodes],
  )

  const filteredAllNodes = useMemo(() => {
    return allNodes.filter((n) => {
      if (filters.status && n.status !== filters.status) return false
      if (filters.company && n.company !== filters.company) return false
      if (filters.site && n.site !== filters.site) return false
      return true
    })
  }, [allNodes, filters])

  const companyTotals = useMemo(() => {
    const counts = new Map<string, number>()
    for (const node of filteredAllNodes) {
      const company = node.company ?? 'Unassigned'
      counts.set(company, (counts.get(company) ?? 0) + 1)
    }
    return counts
  }, [filteredAllNodes])

  const grouped = useMemo(() => {
    const companyMap = new Map<string, NodeSummary[]>()

    for (const node of nodes) {
      const company = node.company ?? 'Unassigned'
      if (!companyMap.has(company)) companyMap.set(company, [])
      companyMap.get(company)!.push(node)
    }

    return Array.from(companyMap.entries())
      .map(([company, companyNodes]) => ({ company, nodes: companyNodes }))
  }, [nodes])

  const onlineCount = allNodes.filter((n) => n.status === 'online').length
  const offlineCount = allNodes.filter((n) => n.status === 'offline').length
  const warningCount = allNodes.filter((n) => n.status === 'warning').length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Nodes</h1>
        <span style={statBadge('#e8f5e9', '#2e7d32')}>{onlineCount} online</span>
        <span style={statBadge('#fff8e1', '#f57f17')}>{warningCount} warning</span>
        <span style={statBadge('#ffebee', '#c62828')}>{offlineCount} offline</span>
      </div>

      <NodeFilterBar filters={filters} companies={companies} sites={sites} onChange={setFilters} />

      {loading && <p style={{ color: '#888' }}>Loading nodes...</p>}
      {error && !loading && (
        <div style={{ color: '#c62828', background: '#fdecea', padding: '10px 14px', borderRadius: 4, marginBottom: 16 }}>
          {error}
          <button
            onClick={fetchNodes}
            style={{ marginLeft: 12, fontSize: 12, cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}
      {!loading && !error && total === 0 && (
        <p style={{ color: '#888', marginTop: 32, textAlign: 'center' }}>No nodes match the current filters.</p>
      )}
      {!loading && !error && total > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {grouped.map((companyGroup) => (
            <section key={companyGroup.company} style={companySectionStyle}>
              <div style={companyHeaderStyle}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{companyGroup.company}</h2>
                <span style={countBadgeStyle}>
                  {companyGroup.nodes.length} shown / {companyTotals.get(companyGroup.company) ?? companyGroup.nodes.length} nodes
                </span>
              </div>
              <NodeTable nodes={companyGroup.nodes} showSiteColumn />
            </section>
          ))}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            itemLabel="nodes"
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  )
}

const companySectionStyle: React.CSSProperties = {
  paddingTop: 4,
}

const companyHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 8,
  paddingBottom: 7,
  borderBottom: '1px solid #e6e6ee',
}

const countBadgeStyle: React.CSSProperties = {
  color: '#777',
  fontSize: 12,
  fontWeight: 500,
}

function statBadge(bg: string, color: string): React.CSSProperties {
  return {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    background: bg,
    color,
  }
}
